<template>
  <div
    class="lp-datepicker"
    :class="[preview && 'lp-datepicker-preview']"
    :data-ua-unique-id="dataUaUniqueId"
  >
    <span
      v-if="labelTop"
      class="lp-input-label font-weight-medium"
      :class="!preview && message ? 'text-error' : ''"
    >
      {{ labelTop }}
      <span v-if="!preview && required" class="text-blue">*</span>
    </span>
    <div class="input-wrapper">
      <lp-input
        ref="inputRef"
        v-model="input"
        type="text"
        :readonly="!typeable"
        v-bind="$attrs"
        :tabindex="disabled ? -1 : 0"
        :status="status"
        suffix-icon="calendar-alt"
        :message="messageValue"
        :disabled="disabled"
        :placeholder="placeholder ?? inputFormat"
        :preview="preview"
        @blur="keyUp"
        @keyup="keyUp"
        @focus="renderView(initialView)"
        @click="renderView(initialView)"
      >
        <template #clear>
          <div v-show="clearable && modelValue && !preview" class="clearable">
            <slot name="clear" :on-clear="clearModelValue">
              <lp-icon
                class="lp-input-icon-wrapper"
                name="close"
                compact
                @click="clearModelValue()"
              />
            </slot>
          </div>
        </template>
      </lp-input>
    </div>
    <year-picker
      v-show="viewShown === 'year'"
      v-model:pageDate="pageDate"
      :selected="modelValue"
      :lower-limit="lowerLimit"
      :upper-limit="upperLimit"
      :picker-zoom-out="pickerZoomOut"
      data-ua-unique-id="ua-datepicker-yearpicker"
      @select="selectYear"
    />
    <month-picker
      v-show="viewShown === 'month'"
      v-model:pageDate="pageDate"
      :selected="modelValue"
      :lower-limit="lowerLimit"
      :upper-limit="upperLimit"
      :format="monthListFormat"
      :heading-format="monthHeadingFormat"
      :locale="locale"
      :picker-zoom-out="pickerZoomOut"
      data-ua-unique-id="ua-datepicker-monthpicker"
      @select="selectMonth"
      @back="viewShown = 'year'"
    />
    <day-picker
      v-show="viewShown === 'day'"
      v-model:pageDate="pageDate"
      :selected="modelValue"
      :week-starts-on="weekStartsOn"
      :lower-limit="lowerLimit"
      :upper-limit="upperLimit"
      :disabled-dates="disabledDates"
      :locale="locale"
      :weekday-format="weekdayFormat"
      :highlights="highlights"
      :picker-zoom-out="pickerZoomOut"
      data-ua-unique-id="ua-datepicker-daypicker"
      @select="selectDay"
      @back="viewShown = 'month'"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watchEffect, PropType } from 'vue'
import { parse, isValid, format } from 'date-fns'
import YearPicker from './YearPicker.vue'
import MonthPicker from './MonthPicker.vue'
import DayPicker from './DayPicker.vue'

const TIME_RESOLUTIONS = ['day', 'month', 'year']
type Highlight = {
  label: string
  date: Date
  color: string
}
export default defineComponent({
  name: 'lp-datepicker',
  components: {
    YearPicker,
    MonthPicker,
    DayPicker,
  },
  inheritAttrs: false,
  props: {
    status: { type: String, default: 'neutral' },
    message: { type: String, default: null },

    /**
     * `v-model` for selected date
     */
    modelValue: {
      type: [Date, Array, null] as PropType<Date | Date[] | null>,
      required: true,
      default: () => null,
    },
    /**
     * Dates not available for picking
     */
    disabledDates: {
      type: Object as PropType<{
        dates?: Date[]
        predicate?: (currentDate: Date) => boolean
      }>,
      required: false,
    },
    /**
     * Upper limit for available dates for picking
     */
    upperLimit: {
      type: Date as PropType<Date>,
      required: false,
    },
    /**
     * Lower limit for available dates for picking
     */
    lowerLimit: {
      type: Date as PropType<Date>,
      required: false,
    },
    /**
     * View on which the date picker should open. Can be either `year`, `month`, or `day`
     */
    startingView: {
      type: String as PropType<'year' | 'month' | 'day'>,
      required: false,
      default: 'day',
      validate: (v: unknown) =>
        typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
    /**
     * `date-fns`-type formatting for a month view heading
     */
    monthHeadingFormat: {
      type: String,
      required: false,
      default: 'LLLL yyyy',
    },
    /**
     * `date-fns`-type formatting for the month picker view
     */
    monthListFormat: {
      type: String,
      required: false,
      default: 'LLL',
    },
    /**
     * `date-fns`-type formatting for a line of weekdays on day view
     */
    weekdayFormat: {
      type: String,
      required: false,
      default: 'EE',
    },
    /**
     * `date-fns`-type format in which the string in the input should be both
     * parsed and displayed
     */
    inputFormat: {
      type: String,
      required: false,
      default: 'MM/dd/yy',
    },
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * [`date-fns` locale object](https://date-fns.org/v2.16.1/docs/I18n#usage).
     * Used in string formatting (see default `monthHeadingFormat`)
     */
    locale: {
      type: Object as PropType<Locale>,
      required: false,
    },
    /**
     * Day on which the week should start.
     *
     * Number from 0 to 6, where 0 is Sunday and 6 is Saturday.
     * Week starts with a Monday (1) by default
     */
    weekStartsOn: {
      type: Number,
      required: false,
      default: 0,
      validator: (value: any) => [0, 1, 2, 3, 4, 5, 6].includes(value),
    },
    /**
     * Disables datepicker and prevents it's opening
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Clears selected date
     */
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /*
     * Allows user to input date manually
     */
    typeable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If set, lower-level views won't show
     */
    minimumView: {
      type: String as PropType<'year' | 'month' | 'day'>,
      required: false,
      default: 'day',
      validate: (v: unknown) =>
        typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
    highlights: {
      type: Array as PropType<Highlight[]>,
      default: (): Highlight[] => [],
    },
    preview: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataUaUniqueId: {
      type: String,
      required: false,
      default: 'ua-datepicker-wrapper',
    },
    labelTop: {
      type: String,
      required: false,
      default: null,
    },
    required: {
      type: Boolean,
    },
    pickerZoomOut: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: {
    'update:modelValue': (value: Date | null | undefined) =>
      value === null || value === undefined || isValid(value),
  },
  setup(props, { emit }) {
    const viewShown = ref('none' as 'year' | 'month' | 'day' | 'none')
    const pageDate = ref<Date>(new Date())
    const inputRef = ref(null)
    const input = ref('')
    const originalMessage = ref(props.message)
    const messageValue = ref(props.message)
    watchEffect(() => {
      const parsed = parse(input.value, props.inputFormat, new Date())
      if (isValid(parsed)) {
        pageDate.value = parsed
        messageValue.value = originalMessage.value
      } else {
        if (input.value.length > props.inputFormat.length) {
          input.value = ''
          emit('update:modelValue', null)
          messageValue.value = 'Date is not valid. Please try again.'
        }
      }
    })

    watchEffect(
      () =>
        (input.value =
          props.modelValue && isValid(props.modelValue)
            ? format(props.modelValue, props.inputFormat, {
                locale: props.locale,
              })
            : ''),
    )
    watchEffect(() => {
      originalMessage.value = props.message
      messageValue.value = originalMessage.value
    })

    const renderView = (view: typeof viewShown.value = 'none') => {
      if (!props.disabled) {
        viewShown.value = view
        if (!props.modelValue) {
          pageDate.value = new Date()
        }
      }
    }
    watchEffect(() => {
      if (props.disabled) viewShown.value = 'none'
    })
    const selectYear = (date: Date) => {
      pageDate.value = date

      if (props.minimumView === 'year') {
        viewShown.value = 'none'
        emit('update:modelValue', date)
      } else {
        viewShown.value = 'month'
      }
    }
    const selectMonth = (date: Date) => {
      pageDate.value = date

      if (props.minimumView === 'month') {
        viewShown.value = 'none'
        emit('update:modelValue', date)
      } else {
        viewShown.value = 'day'
      }
    }
    const selectDay = (date: Date) => {
      emit('update:modelValue', date)

      viewShown.value = 'none'
    }
    const clearModelValue = () => {
      if (props.clearable) {
        emit('update:modelValue', null)
      }
    }

    const keyUp = (event: KeyboardEvent | MouseEvent) => {
      const code = event.code ? event.code : event.button
      // close calendar if escape or enter are pressed
      const closeButton = [
        27, // escape
        13, // enter
      ].includes(code as number)

      if (closeButton) {
        inputRef.value?.blur()
      }
      if (props.typeable && input.value.length === props.inputFormat.length) {
        const parsedDate = parse(input.value, props.inputFormat, new Date())
        if (isValid(parsedDate)) {
          emit('update:modelValue', parsedDate)
        } else {
          emit('update:modelValue', null)
          input.value = ''
        }
      }
      renderView()
    }

    const initialView = computed(() => {
      const startingViewOrder = TIME_RESOLUTIONS.indexOf(props.startingView)
      const minimumViewOrder = TIME_RESOLUTIONS.indexOf(props.minimumView)

      return startingViewOrder < minimumViewOrder
        ? props.minimumView
        : props.startingView
    })

    return {
      input,
      inputRef,
      pageDate,
      renderView,
      selectYear,
      selectMonth,
      selectDay,
      keyUp,
      viewShown,
      clearModelValue,
      initialView,
      messageValue,
    }
  },
})
</script>
